"use client"
import '@/app/landing/landing.css'
import { useSearchParams } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'

export const dynamic = "force-dynamic";

export default function LoginContent() {
  const params = useSearchParams()
  const cb = params.get('callbackUrl') || ''
  const alert = params.get('alert')

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sentTo, setSentTo] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [previewUrl, setPreviewUrl] = useState<string | undefined>()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    document.body.classList.add('landing')
    return () => { document.body.classList.remove('landing') }
  }, [])

  useEffect(() => {
    if (!sentTo) inputRef.current?.focus({ preventScroll: true } as FocusOptions)
  }, [sentTo])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError(undefined)
    try {
      const body: Record<string, string> = { email }
      if (cb) body.callbackUrl = cb
      const r = await fetch('/api/auth/magic-link/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || 'Failed to send magic link')
      setSentTo(email)
      if (j.previewUrl) setPreviewUrl(j.previewUrl)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="page">
      <header className="top-kicker">
        <div className="kicker">The key to success?</div>
      </header>

      <section className="hero">
        <div className="stack">
          <h1 className="line l1">Execution<span className="dot">.</span></h1>
          <h1 className="line l2">Execution<span className="dot">.</span></h1>
          <h1 className="line l3">Execution<span className="dot">.</span></h1>
        </div>
      </section>

      <section className="cta">
        <div className="cta-inner">
          <h2 className="cta-headline">
            <span className="brand">Fuggr</span> is your <span className="under">daily execution system</span><span className="dot">.</span>
          </h2>

          {!sentTo && (alert === 'expired' || alert === 'resend') && (
            <div className="alert-banner">
              This link has expired. We&apos;ve sent you a new one — check your inbox (or spam).
            </div>
          )}

          {!sentTo ? (
            <form className="form" onSubmit={handleSubmit}>
              <span className="ic" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2"/>
                  <path d="M3 7l9 6 9-6"/>
                </svg>
              </span>
              <input
                ref={inputRef}
                type="email"
                placeholder="Enter email to log in or sign up"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
              <button type="submit" disabled={loading}>
                {loading ? (
                  <span>Sending…</span>
                ) : (
                  <><span>Continue</span><span className="arrow">→</span></>
                )}
              </button>
            </form>
          ) : (
            <form className="form">
              <span className="ic" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2"/>
                  <path d="M3 7l9 6 9-6"/>
                </svg>
              </span>
              <input type="email" value={sentTo} readOnly />
              <button type="button" disabled>
                <span>Check your inbox</span><span className="arrow">✓</span>
              </button>
            </form>
          )}

          {error && <p className="form-error">{error}</p>}

          <div className="form-note">
            {!sentTo ? (
              <><span className="pip" />{' '}No password. No credit card. No spam.</>
            ) : (
              <><span className="pip" />{' '}We sent a link to <strong>{sentTo}</strong> — check your inbox (or spam).</>
            )}
          </div>

          {previewUrl && (
            <a href={previewUrl} style={{ fontSize: 12, color: 'rgba(241,237,228,0.5)', textDecoration: 'underline' }} target="_blank" rel="noreferrer">
              Preview email (Ethereal)
            </a>
          )}
        </div>
      </section>
    </div>
  )
}
